/**
 * Portal Read Path v0.1 — Zoho Deal layouts, field maps, freshness contract.
 * Canonical spec: docs/portal-read-path-v0.1.md (§B–§F).
 */

/** §D — Zoho Deal data freshness is enforced at the fetcher layer, not per UI call site. */
export const ZOHO_DEAL_FRESHNESS_MS = 60_000;
/** §D.2 — stale threshold equals poll interval in v0.1 (canonical spec). */
export const ZOHO_DEAL_STALE_MS = 60_000;

export const ZOHO_DEAL_LAYOUT = {
  PNW_PROJECTS: '5334978000009007922',
  FIELD_DEPLOYMENT: '5334978000011935003',
} as const;

export type ZohoDealLayoutKey = keyof typeof ZOHO_DEAL_LAYOUT;

export const ZOHO_DEAL_LINK_FIELD = {
  PNW_PROJECTS: 'CAM_Supabase_Project_ID',
  FIELD_DEPLOYMENT: 'FD_Supabase_Project_ID',
} as const;

/** §B — PNW Projects layout regions (§B.5 install fields are a separate route, not generic card). */
export const PNW_DEAL_FIELD_REGIONS = {
  header: ['Deal_Name', 'Account_Name'],
  portalStatus: ['CAM_Portal_Status'],
  salesStage: ['Stage'],
  scheduleCommercial: ['Closing_Date', 'Amount'],
  supplement: ['Description'],
  link: [ZOHO_DEAL_LINK_FIELD.PNW_PROJECTS],
} as const;

/** §C — Field Deployment layout regions. */
export const FD_DEAL_FIELD_REGIONS = {
  header: ['Deal_Name', 'Account_Name'],
  portalStatus: ['FD_Portal_Status'],
  salesStage: ['Stage'],
  scopeTags: [
    'Deployment_Category',
    'Deployment_Intent',
    'Primary_Deployment_Sector',
    'Hotel_Segment',
    'Trip_Complexity',
  ],
  tripDates: ['Trip_Start_Date', 'Trip_End_Date'],
  salesClose: ['Closing_Date'],
  salesOwner: ['Owner'],
  crewLead: ['Engineer_s_Assigned'],
  supplement: ['Description'],
  relatedTrips: ['Related_Trip_Deal'],
  link: [ZOHO_DEAL_LINK_FIELD.FIELD_DEPLOYMENT],
} as const;

export const PNW_DEAL_READ_FIELDS = [
  'id',
  'Modified_Time',
  'Layout',
  ...new Set(Object.values(PNW_DEAL_FIELD_REGIONS).flat()),
] as const;

export const FD_DEAL_READ_FIELDS = [
  'id',
  'Modified_Time',
  'Layout',
  ...new Set(Object.values(FD_DEAL_FIELD_REGIONS).flat()),
] as const;

/**
 * §C.10 — numbered FD trip stages (Zoho `Stage` picklist → progress strip).
 * `zoho` is the exact Stage value; `short` is the portal strip label.
 */
export const FD_TRIP_PROGRESS_STAGES = [
  { zoho: '1. Lead/Inquiry – Trip Requested', short: 'Requested' },
  { zoho: '2. Discovery & Site Pre-Assessment', short: 'Discovery' },
  { zoho: '3. Solution Architecture & Proposal', short: 'Proposal' },
  { zoho: '4. Commercial Review & Approval', short: 'Approval' },
  { zoho: '5. Scheduled Onsite Deployment', short: 'Scheduled' },
  { zoho: '6. In-Progress Implementation', short: 'On site' },
  { zoho: '7. QA, Handover & Training', short: 'Handover' },
  { zoho: '8. Converted to Managed Services', short: 'Managed' },
  { zoho: '9. Closed Won – Project Only', short: 'Closed won' },
  { zoho: '10. Closed Lost / Cancelled Trip', short: 'Closed lost' },
] as const;

/** §B.5 — CAM install workflow panel only (not generic PNW project card). */
export const PNW_CAM_INSTALL_WORKFLOW_FIELDS = [
  'CAM_Portal_Status',
  'CAM_Property_Name',
  'CAM_Property_Address',
  'CAM_Power_Option',
  'CAM_Fiber_or_AirFiber',
  'CAM_Total_Cameras',
  'CAM_LPR_Camera_Count',
  'CAM_LPR_Camera_Locations',
  'CAM_Survey_Completed_Date',
  'CAM_Survey_SharePoint_URL',
  'CAM_Proposal_Sent_Date',
  'CAM_Proposal_Viewed_Date',
  'CAM_Proposal_Signed_Date',
  'CAM_Signer_Name',
  'CAM_Signer_Title',
  'CAM_SOW_Document_URL',
  'CAM_Deposit_Invoice_Number',
  'CAM_Deposit_Paid_Date',
  'CAM_Deposit_Amount',
  'CAM_Install_Scheduled_Date',
  'CAM_Install_Completed_Date',
  'CAM_Final_Invoice_Number',
  'CAM_Final_Invoice_Paid_Date',
  'CAM_Maintenance_Agreement_Sent',
  'CAM_Maintenance_Agmt_Signed',
] as const;

/** §B.1–§B.4 fields not on generic PNW card (v0.1 has no generic PNW card). */
export const PNW_DEAL_UNMAPPED_FIELDS = [
  'Contact_Name',
  'Owner',
  'Project_Start',
  'Project_End',
  'Quote_Date',
  'Expected_Revenue',
  'Probability',
  'Type',
  'Lead_Source',
  'CAM_Zoho_Projects_ID',
] as const;

/** §C fields not on customer-portal ActiveFieldProjectCard (v0.1). */
export const FD_DEAL_UNMAPPED_FIELDS = [
  'Contact_Name',
  'On_Site_Days',
  'Travel_Days',
  'Amount',
  'Equipment_Estimate_Number',
  'Equipment_Cost',
  'Labor_Cost',
  'Recurring_Revenue_Attached',
  'ARR_Expected',
  'Managed_Service_Package_Targeted',
  'Room_Count',
  'Meeting_Space_sq_ft',
  'APs_Touched',
  'Cameras_Touched',
  'Doors_Touched',
  'Displays_Touched',
  'Post_Deployment_Owner',
  'FD_Survey_SharePoint_URL',
] as const;

export function listUnmappedZohoDealFields(layout: ZohoDealLayoutKey): readonly string[] {
  return layout === 'PNW_PROJECTS' ? PNW_DEAL_UNMAPPED_FIELDS : FD_DEAL_UNMAPPED_FIELDS;
}

/** §C.10 — index 0–9 for numbered FD stages; -1 = generic Stage / not started. */
export function fdTripProgressIndex(stage: string | null | undefined): number {
  if (!stage) return -1;
  const exact = FD_TRIP_PROGRESS_STAGES.findIndex((s) => s.zoho === stage);
  if (exact >= 0) return exact;
  const numbered = stage.match(/^(\d+)\./);
  if (numbered) {
    const n = Number(numbered[1]);
    if (n >= 1 && n <= 10) return n - 1;
  }
  return -1;
}

export interface ZohoDealFreshness {
  modifiedTime: string | null;
  fetchedAt: string;
  expectedRefreshMs: typeof ZOHO_DEAL_FRESHNESS_MS;
  staleMs: typeof ZOHO_DEAL_STALE_MS;
}

export interface ZohoCrmUserRef {
  id: string;
  name: string;
}

export interface ZohoRelatedTripSummary {
  dealId: string;
  dealName: string | null;
  portalStatus: string | null;
  salesStage: string | null;
  tripStartDate: string | null;
  tripEndDate: string | null;
  supabaseTripId: string | null;
  isCurrent: boolean;
}

export interface ZohoFdDealCommercialContext {
  dealId: string;
  dealName: string | null;
  /** §C.9 — primary portal-facing status chip. */
  portalStatus: string | null;
  /** §C.9 — secondary, always labeled "Sales stage". */
  salesStage: string | null;
  accountName: string | null;
  scopeTags: {
    deploymentCategory: string[];
    deploymentIntent: string | null;
    primaryDeploymentSector: string | null;
    hotelSegment: string | null;
    tripComplexity: string | null;
  };
  description: string | null;
  tripStartDate: string | null;
  tripEndDate: string | null;
  salesCloseDate: string | null;
  salesOwnerName: string | null;
  engineersAssigned: ZohoCrmUserRef[];
  relatedTripDealId: string | null;
  relatedTrips: ZohoRelatedTripSummary[];
  layoutId: string;
  freshness: ZohoDealFreshness;
}

export interface ZohoDealReadResponse {
  deal: ZohoFdDealCommercialContext | null;
  idempotencyWritten: boolean;
}

export type ZohoDealFieldConflict = {
  field: 'start_date' | 'end_date' | 'lead' | 'portal_status';
  supabaseValue: string | null;
  zohoValue: string | null;
};
